import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from 'yup';
import { userRequest } from '../../utils/requestMethods';
import { ButtonLoading } from '../../components';
import ErrorHandler from '../../utils/errorHandler';
import { getEducationLevelLabel } from '../../utils/enrollmentUtils';

// TODO: Replace with actual Play Store URL when app is published
const PARENT_APP_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.acceede.acceede_mvp';

const inputBase =
  "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:text-gray-400";
const inputError =
  "w-full px-4 py-2.5 bg-white border border-red-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent transition-all";

const FieldError = ({ message }) =>
  message ? (
    <p className="flex items-center gap-1 text-xs text-red-500 mt-1">
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {message}
    </p>
  ) : null;

const SubmissionSuccess = ({ schoolName }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
    <div className="max-w-md w-full text-center">
      <div className="flex justify-center mb-6">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>
      <h1 className="text-2xl font-bold text-slate-800 mb-2">Application Submitted!</h1>
      <p className="text-slate-500 mb-2">Your enrollment application has been received.</p>
      {schoolName && <p className="text-indigo-600 font-medium text-sm mb-8">{schoolName}</p>}
      <div className="border-t border-slate-200 my-8" />
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center">
            <svg className="w-7 h-7 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 15h3" />
            </svg>
          </div>
        </div>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Stay in the loop</h2>
        <p className="text-sm text-slate-500 mb-5">
          Download the parent app to track your child's attendance, results, fees, and school updates — all in one place.
        </p>
        <a href={PARENT_APP_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
          <button type="button" className="inline-flex items-center gap-3 bg-indigo-800 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl transition-colors w-full justify-center">
            <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.18 23.76c.37.2.8.22 1.18.06l11.52-6.45-2.54-2.54-10.16 8.93zM.44 1.06C.17 1.4 0 1.88 0 2.49v19.02c0 .61.17 1.09.44 1.43l.07.07 10.66-10.66v-.25L.51.99.44 1.06zM19.69 10.26l-2.74-1.53-2.85 2.85 2.85 2.85 2.76-1.54c.79-.44.79-1.19-.02-1.63zM4.36.18L15.88 6.63l-2.54 2.54L3.18.24C3.56.08 3.99.1 4.36.18z" />
            </svg>
            <div className="text-left">
              <div className="text-xs text-slate-300 leading-none">GET IT ON</div>
              <div className="text-sm font-semibold leading-tight">Google Play</div>
            </div>
          </button>
        </a>
      </div>
      <p className="text-xs text-slate-400 mt-6">You will receive a confirmation email once your application is reviewed.</p>
    </div>
  </div>
);

const ParentEnrollmentForm = () => {
  const { schoolSlug, levelOfEducation } = useParams();
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [formTemplate, setFormTemplate] = useState(null);
  const [classes, setClasses] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const handleFileUpload = async (file, fieldName, fieldIndex) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
    ];

    if (file.size > maxSize) { setError('File size must be less than 10MB'); return; }
    if (!allowedTypes.includes(file.type)) {
      setError('File type not supported. Please upload PDF, DOC, DOCX, JPG, PNG, or GIF files.');
      return;
    }

    const fieldKey = `${fieldName}_${fieldIndex}`;
    try {
      setUploadingFiles(prev => ({ ...prev, [fieldKey]: true }));
      setUploadProgress(prev => ({ ...prev, [fieldKey]: 0 }));
      setError('');

      const formData = new FormData();
      formData.append('attachments', file);

      const response = await userRequest.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [fieldKey]: progress }));
        },
      });

      const fieldPath = `fields.${fieldIndex}.value`;
      const fileData = Array.isArray(response.data) ? response.data[0] : response.data;
      const fileUrl = fileData?.url;

      if (fileUrl) {
        formik.setFieldValue(fieldPath, fileUrl);
      } else {
        throw new Error('No file URL returned from server. Please try again.');
      }

      setUploadingFiles(prev => ({ ...prev, [fieldKey]: false }));
      setUploadProgress(prev => ({ ...prev, [fieldKey]: 100 }));
    } catch (err) {
      setUploadingFiles(prev => ({ ...prev, [fieldKey]: false }));
      setUploadProgress(prev => ({ ...prev, [fieldKey]: 0 }));
      setError('Failed to upload file. Please try again.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFormLoading(true);
        const templateResponse = await userRequest.get(`/schools/${schoolSlug}/${levelOfEducation}/enrol-form-template`);
        setFormTemplate(templateResponse.data);

        try {
          const classesResponse = await userRequest.get(`/classes/slug/${schoolSlug}?limit=100&level_of_education=${levelOfEducation}`);
          setClasses(classesResponse.data.classes || classesResponse.data || []);
        } catch {
          try {
            const altClassesResponse = await userRequest.get(`/classes?slug=${schoolSlug}`);
            setClasses(altClassesResponse.data.classes || altClassesResponse.data || []);
          } catch {
            setClasses([]);
          }
        }
      } catch (err) {
        setError("Could not load enrollment form. Please try again later.");
      } finally {
        setFormLoading(false);
      }
    };

    if (schoolSlug && levelOfEducation) fetchData();
  }, [schoolSlug, levelOfEducation]);

  const validationSchema = Yup.object().shape({
    user_email: Yup.string().email('Invalid email address').required('Email address is required'),
    fields: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(),
        value: Yup.string().test('required', 'This field is required', function (value) {
          const field = this.parent;
          const fieldConfig = formTemplate?.sections
            ?.flatMap(section => section.fields)
            .find(f => f.name === field.name);
          return !fieldConfig?.required || (fieldConfig?.required && value);
        }),
      })
    ),
  });

  const formik = useFormik({
    initialValues: {
      user_email: '',
      fields:
        formTemplate?.sections?.flatMap(section =>
          section.fields.map(field => ({
            name: field.name,
            value: field.type === 'radio' ? 'false' : '',
            required: field.required,
          }))
        ) || [],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const filteredValues = {
          user_email: values.user_email,
          fields: values.fields
            .map(field => {
              const fieldConfig = formTemplate?.sections
                ?.flatMap(section => section.fields)
                .find(f => f.name === field.name);
              let value = field.value;
              if (field.name === 'consent') value = value === 'true';
              return { name: field.name, value };
            })
            .filter(field => {
              const fieldConfig = formTemplate?.sections
                ?.flatMap(section => section.fields)
                .find(f => f.name === field.name);
              if (fieldConfig?.required) return true;
              if (typeof field.value === 'string') return field.value.trim() !== '';
              if (typeof field.value === 'boolean') return true;
              return !!field.value;
            }),
        };

        await userRequest.post(`/schools/${schoolSlug}/${levelOfEducation}/enrol-submission`, filteredValues);
        setSubmitted(true);
      } catch (err) {
        ErrorHandler.handleError(err, {
          showToast: true,
          logToConsole: true,
          onValidationError: (error) => {
            setError(error.message);
            setTimeout(() => setError(""), 3000);
          },
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const renderFormField = (field, sectionIndex, fieldIndex) => {
    const flattenedIndex =
      formTemplate.sections
        .slice(0, sectionIndex)
        .reduce((acc, section) => acc + section.fields.length, 0) + fieldIndex;

    const fieldPath = `fields[${flattenedIndex}].value`;
    const fieldValue = formik.values.fields[flattenedIndex]?.value || "";
    const errorMsg =
      formik.touched.fields?.[flattenedIndex]?.value &&
      formik.errors.fields?.[flattenedIndex]?.value;
    const inputCls = errorMsg ? inputError : inputBase;

    const Label = () => (
      <label className="block mb-1.5 text-sm font-medium text-gray-700">
        {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    );

    if (field.name === 'class') {
      return (
        <div key={field._id}>
          <Label />
          <select
            name={fieldPath}
            value={fieldValue}
            onChange={(e) => formik.setFieldValue(fieldPath, e.target.value)}
            onBlur={formik.handleBlur}
            className={inputCls}
            required={field.required}
          >
            <option value="">Select class</option>
            {classes.length > 0
              ? classes.map((cls) => <option key={cls._id} value={cls._id}>{cls.class_name}</option>)
              : <option value="" disabled>No classes available</option>
            }
          </select>
          <FieldError message={errorMsg} />
        </div>
      );
    }

    if (field.type === 'radio') {
      return (
        <div key={field._id} className="col-span-full">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name={fieldPath}
              value="true"
              checked={fieldValue === "true"}
              onChange={(e) => formik.setFieldValue(fieldPath, e.target.value)}
              onBlur={formik.handleBlur}
              className="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              required={field.required}
            />
            <span className="text-sm text-gray-700">
              {field.placeholder}{field.required && <span className="text-red-500 ml-0.5">*</span>}
            </span>
          </label>
          <FieldError message={errorMsg} />
        </div>
      );
    }

    if (field.type === 'file') {
      const fieldKey = `${field.name}_${flattenedIndex}`;
      const isUploading = uploadingFiles[fieldKey];
      const progress = uploadProgress[fieldKey] || 0;

      return (
        <div key={field._id} className="col-span-full">
          <Label />
          <input
            type="file"
            name={fieldPath}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) handleFileUpload(file, field.name, flattenedIndex);
            }}
            onBlur={formik.handleBlur}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 border border-gray-200 rounded-lg cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required={field.required && !fieldValue}
            disabled={isUploading}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
          />
          <p className="mt-1 text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG, GIF · Max 10MB</p>

          {isUploading && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Uploading...</span><span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {fieldValue && !isUploading && (
            <div className="mt-2 flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-xs font-medium text-green-800">File uploaded successfully</p>
                  <p className="text-xs text-green-600 truncate max-w-xs">{fieldValue.split('/').pop()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <a href={fieldValue} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                <button
                  type="button"
                  onClick={() => {
                    formik.setFieldValue(fieldPath, '');
                    const fileInput = document.querySelector(`input[name="${fieldPath}"]`);
                    if (fileInput) fileInput.value = '';
                  }}
                  className="text-xs text-red-500 hover:underline"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
          <FieldError message={errorMsg} />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field._id}>
          <Label />
          <select
            name={fieldPath}
            value={fieldValue}
            onChange={(e) => formik.setFieldValue(fieldPath, e.target.value)}
            onBlur={formik.handleBlur}
            className={inputCls}
            required={field.required}
          >
            <option value="">Select {field.label.toLowerCase()}</option>
            {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <FieldError message={errorMsg} />
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field._id} className="col-span-full">
          <Label />
          <textarea
            name={fieldPath}
            value={fieldValue}
            onChange={(e) => formik.setFieldValue(fieldPath, e.target.value)}
            onBlur={formik.handleBlur}
            className={`${inputCls} resize-none`}
            rows={3}
            required={field.required}
            placeholder={field.placeholder}
          />
          <FieldError message={errorMsg} />
        </div>
      );
    }

    return (
      <div key={field._id}>
        <Label />
        <input
          type={field.type}
          name={fieldPath}
          value={fieldValue}
          onChange={(e) => formik.setFieldValue(fieldPath, e.target.value)}
          onBlur={formik.handleBlur}
          className={inputCls}
          required={field.required}
          placeholder={field.placeholder}
        />
        <FieldError message={errorMsg} />
      </div>
    );
  };

  if (formLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-600 border-t-transparent" />
        <p className="text-sm text-gray-400">Loading enrollment form...</p>
      </div>
    );
  }

  if (!formTemplate) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50 px-4 text-center">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <p className="font-semibold text-gray-700">Could not load enrollment form</p>
        <p className="text-sm text-gray-400">Please check the link or try again later.</p>
      </div>
    );
  }

  if (submitted) return <SubmissionSuccess schoolName={formTemplate?.school_name} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header banner */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 px-6 py-12 text-white text-center">
        <div className="max-w-2xl mx-auto">
          {formTemplate.school_name && (
            <p className="text-indigo-300 text-xs font-semibold uppercase tracking-widest mb-3">
              {formTemplate.school_name}
            </p>
          )}
          <h1 className="text-3xl font-bold mb-3">{formTemplate.title}</h1>
          {formTemplate.description && (
            <p className="text-indigo-200 text-sm leading-relaxed max-w-lg mx-auto">
              {formTemplate.description}
            </p>
          )}
          {levelOfEducation && (
            <span className="inline-flex items-center mt-4 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white">
              {getEducationLevelLabel(levelOfEducation)}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={formik.handleSubmit} className="space-y-5">
          {/* Contact Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                1
              </span>
              <h3 className="font-semibold text-gray-800">Contact Information</h3>
            </div>
            <div className="px-6 py-5">
              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">
                  Email Address<span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="email"
                  name="user_email"
                  value={formik.values.user_email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter your email address"
                  className={formik.touched.user_email && formik.errors.user_email ? inputError : inputBase}
                />
                <FieldError
                  message={formik.touched.user_email && formik.errors.user_email}
                />
              </div>
            </div>
          </div>

          {/* Dynamic sections */}
          {formTemplate.sections.map((section, sectionIndex) => (
            <div key={section._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {sectionIndex + 2}
                </span>
                <div>
                  <h3 className="font-semibold text-gray-800">{section.title}</h3>
                  {section.description && (
                    <p className="text-xs text-gray-400 mt-0.5">{section.description}</p>
                  )}
                </div>
              </div>
              <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map((field, fieldIndex) =>
                  renderFormField(field, sectionIndex, fieldIndex)
                )}
              </div>
            </div>
          ))}

          {/* Submit */}
          <div className="flex justify-end pt-2 pb-8">
            {loading ? (
              <ButtonLoading styles="inline-flex items-center justify-center py-3 px-8 rounded-xl text-white bg-indigo-400 font-semibold text-sm" />
            ) : (
              <button
                type="submit"
                className="inline-flex items-center gap-2 py-3 px-8 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Submit Enrollment Form
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ParentEnrollmentForm;
